import { defineStore } from "pinia";
import { useContentReadStore } from "./ContentReadStore";
import { useLoginStore } from "./LoginStore";
import { useErrorStore } from "./ErrorStore";
import { usePlanStore } from "./PlanStore";
import type { IContentsRepository } from "~/interfaces/IContentsRepository";
import { ContentsRepository } from "~/repositories/ContentsRepository";
import { UserContentsInfo } from "~/entities/UserContentsInfo";
import { ContentReadInfo } from "~/entities/ContentReadInfo";
import { CONTENT_TYPE, CONTENT_TYPE_LIST } from "~/consts/contentsConfig";
import type { ArticleInfo } from "~/entities/ArticleInfo";
import type { DocumentInfo } from "~/entities/DocumentInfo";
import type { MovieInfo } from "~/entities/MovieInfo";
import type { LiveInfo } from "~/entities/LiveInfo";

export const useContentsStore = defineStore("ContentsStore", () => {
  const contentReadStore = useContentReadStore();
  const loginStore = useLoginStore();
  const errorStore = useErrorStore();
  const planStore = usePlanStore();
  const repository: IContentsRepository = new ContentsRepository();

  const userContentsInfo = ref<UserContentsInfo | undefined>();
  const prevUserId = ref<string | undefined>();
  const prevContentsId = ref<string | undefined>();

  // 未読管理用のフラグ
  const hasUnreadArchive = ref(false); // LIVEアーカイブ未読フラグ
  const hasUnreadMovie = ref(false); // 動画未読フラグ
  const hasUnreadDocument = ref(false); // 資料未読フラグ
  const hasUnreadLive = ref(false); // LIVE未読フラグ
  const hasUnreadSchedule = ref(false); // スケジュール未読フラグ
  const hasUnreadArticle = ref(false); // 記事未読フラグ

  // ライブメニューの未読フラグ
  const hasUnreadForLiveMenu = computed(() => {
    return hasUnreadArchive.value || hasUnreadSchedule.value || hasUnreadLive.value;
  });

  // プランタブの未読フラグ
  const hasUnreadForPlanTab = computed(() => {
    return (
      hasUnreadArchive.value ||
      hasUnreadMovie.value ||
      hasUnreadDocument.value ||
      hasUnreadLive.value ||
      hasUnreadSchedule.value ||
      hasUnreadArticle.value
    );
  });

  /**
   * コンテンツの公開非公開情報を変更する
   */
  const putPublish = async (contentsId: string, publishStatus: string) => {
    await repository.putPublish(contentsId, publishStatus);
  };

  /**
   * コンテンツ閲覧
   */
  const postBrowse = async (contentsId: string, userId: string) => {
    await repository.postBrowse(contentsId, userId);
  };

  /**
   * コンテンツ視聴完了
   */
  const postComplete = async (contentsId: string) => {
    await repository.postComplete(contentsId);
  };

  /**
   * いいねする
   */
  const postGood = async (contentsId: string) => {
    await repository.postGood(contentsId);
  };

  /**
   * いいね解除
   */
  const deleteGood = async (contentsId: string) => {
    await repository.deleteGood(contentsId);
  };

  /**
   * ブックマークする
   */
  const postBookmark = async (contentsId: string) => {
    await repository.postBookmark(contentsId);
  };

  /**
   * ブックマーク解除
   */
  const deleteBookmark = async (contentsId: string) => {
    await repository.deleteBookmark(contentsId);
  };

  /**
   * ユーザーごとのコンテンツ情報を取得
   */
  const fetchUserContentsInfo = async (userId: string, contentsId: string) => {
    userContentsInfo.value = await repository.getUserContentsInfo(userId, contentsId);
    prevUserId.value = userId;
    prevContentsId.value = contentsId;
  };

  /**
   * ユーザーごとのコンテンツ情報を取得
   */
  const getUserContentsInfo = async (userId: string, contentsId: string): Promise<UserContentsInfo> => {
    return await repository.getUserContentsInfo(userId, contentsId);
  };

  /**
   * ユーザーごとのコンテンツ情報を設定
   */
  const putUserContentsInfo = async (userId: string, contentsId: string, info: UserContentsInfo) => {
    await repository.putUserContentsInfo(userId, contentsId, info);
  };

  /**
   * ユーザーごとのコンテンツリストを取得
   */
  const getUserContentsList = async (userId: string, contentsIds: string[]) => {
    return await repository.getUserContentsList(userId, contentsIds);
  };

  /**
   * ユーザーごとのコンテンツリストを一括取得
   */
  const getUserContentsListAll = async (userId: string, contentsIds: string[]) => {
    return await repository.getUserContentsListAll(userId, contentsIds);
  };

  /**
   * ユーザーごとのcurrentTimeを更新。maxTimeも更新できたら更新する
   */
  const putCurrentTime = async (
    userId: string,
    contentsId: string,
    currentTime: number,
    duration: number | undefined,
    isComplete: boolean | undefined,
  ) => {
    // 前回の値と比較するためデータを取得する
    await fetchUserContentsInfo(userId, contentsId);

    const info = userContentsInfo.value ?? UserContentsInfo.empty();
    info.currentTime = currentTime;
    info.maxTime = Math.max(currentTime, info.maxTime);

    await repository.putUserContentsInfo(userId, contentsId, info);

    // 視聴完了の場合は視聴完了APIをコールする
    if (!isComplete && duration && info.maxTime >= duration) {
      await postComplete(contentsId);
    }

    // 通信量削減のためfirestoreから取得ではなく、ローカルの値を更新する
    userContentsInfo.value = info;
  };

  /**
   * ユーザーごとのmaxPageを更新
   */
  const putMaxPage = async (
    userId: string,
    contentsId: string,
    maxPage: number,
    totalPage: number,
    isComplete: boolean | undefined,
  ) => {
    // 視聴完了の場合はなにもしない
    if (isComplete) return;

    // 前回の値と比較するためデータを取得する
    if (prevUserId.value !== userId || prevContentsId.value !== contentsId) {
      await fetchUserContentsInfo(userId, contentsId);
    }

    const info = userContentsInfo.value ?? UserContentsInfo.empty();

    // 既にmaxPageが大きい場合は更新しない
    if (info.maxPage >= maxPage) return;

    info.maxPage = Math.max(maxPage, info.maxPage);

    await repository.putUserContentsInfo(userId, contentsId, info);

    // 視聴完了の場合は視聴完了APIをコールする
    if (!isComplete && info.maxPage >= totalPage) {
      await postComplete(contentsId);
    }

    // 通信量削減のためfirestoreから取得ではなく、ローカルの値を更新する
    userContentsInfo.value = info;
  };

  /**
   * コンテンツ情報を取得
   * @param page ページ番号
   * @param pageSize 1ページあたりの件数
   * @param options contents_types: コンテンツタイプリスト([movie/live_movie/archive_movie/archive_movie_progress/document/schedule])
   * @param options exclusion_contents_id: 除外するコンテンツID
   * @param options is_contracted: 契約済みのコンテンツのみ取得するか
   * @param options is_free: 無料のコンテンツのみ取得するか
   * @param options owner_user_id: コンテンツの所有者のユーザーID
   * @param options plan_id: プランID
   * @param options search_word: 検索キーワード
   * @param options sort: ソート順[desc/asc]（nullの場合desc)
   * @param options tag: タグ
   * @param options user_role: ユーザーのロール
   * @param options skipUnreadCheck: 未読チェックをスキップするか。未読チェックは30個までという制限があるのでそれ以上取得する場合はtrueにする
   */
  const getList = async <T>(page: number, pageSize: number, options?: Record<string, any>): Promise<T> => {
    interface ContentsList {
      contents: {
        id: number;
        isUnread: boolean;
        contentsType: string;
        limitedPlanPriceIds?: number[];
        isLimitedByPlanPrice?: boolean;
      }[];
    }

    const skipUnreadCheck = options?.skipUnreadCheck;
    // skipUnreadCheckはAPIリクエストには使用しないのでoptionsから削除
    if (options) {
      delete options.skipUnreadCheck;
    }

    const contents = (await repository.getList<T>(page, pageSize, options)) as ContentsList;

    // ログイン中の場合、ユーザーごとのコンテンツ既読状態を付与
    const userId = loginStore.loginInfo?.userId;
    if (userId && skipUnreadCheck !== true && contents.contents.length > 0) {
      // ユーザーの契約プランIDを取得
      const userPlanPriceId = planStore.plan?.contractStatus?.planPriceId;

      // Firestore からユーザーコンテンツリスト取得
      const userContents = await repository.getUserContentsList(
        userId,
        contents.contents.map(content => content.id.toString()),
      );

      const unreadList: { [key: string]: boolean } = {
        [CONTENT_TYPE.LIVE_ARCHIVE]: false,
        [CONTENT_TYPE.MOVIE]: false,
        [CONTENT_TYPE.DOCUMENT]: false,
        [CONTENT_TYPE.LIVE_MOVIE]: false,
        [CONTENT_TYPE.LIVE_SCHEDULE]: false,
        [CONTENT_TYPE.ARTICLE]: false,
      };

      // 負荷削減のためMapを使用
      const userContentsMap = new Map(userContents.map(userContent => [userContent.id.toString(), userContent]));

      // ユーザーごとのコンテンツ既読状態とプラン制限を付与
      contents.contents.forEach(content => {
        const userContent = userContentsMap.get(content.id.toString());

        // オーナーの場合は常に閲覧可能
        const isOwner = isOwnerContent(content as ArticleInfo | DocumentInfo | MovieInfo | LiveInfo);
        if (isOwner) {
          content.isUnread = !userContent?.read;
          content.isLimitedByPlanPrice = false;
          return;
        }

        // プラン制限の判定
        content.isLimitedByPlanPrice = isContentLimitedByPlanPrice(
          content.limitedPlanPriceIds,
          userPlanPriceId,
          isOwner,
        );

        // 制限されている場合は未読判定をスキップ
        if (content.isLimitedByPlanPrice) {
          content.isUnread = false;
        } else {
          content.isUnread = !userContent?.read;
        }
      });

      contents.contents.forEach(content => {
        if (content && content.isUnread) {
          unreadList[content.contentsType] = true;
        }
      });

      hasUnreadArchive.value = hasUnreadArchive.value || unreadList[CONTENT_TYPE.LIVE_ARCHIVE];
      hasUnreadMovie.value = hasUnreadMovie.value || unreadList[CONTENT_TYPE.MOVIE];
      hasUnreadDocument.value = hasUnreadDocument.value || unreadList[CONTENT_TYPE.DOCUMENT];
      hasUnreadLive.value = hasUnreadLive.value || unreadList[CONTENT_TYPE.LIVE_MOVIE];
      hasUnreadSchedule.value = hasUnreadSchedule.value || unreadList[CONTENT_TYPE.LIVE_SCHEDULE];
      hasUnreadArticle.value = hasUnreadArticle.value || unreadList[CONTENT_TYPE.ARTICLE];
    }
    return contents as T;
  };

  /**
   * コンテンツ詳細を取得する
   */
  const getDetail = async <T>(contentsId: string): Promise<T | undefined> => {
    // コンテンツ取得に失敗した場合には無効なURLエラーダイアログを表示する
    try {
      return (await repository.getDetail(contentsId)) as T;
    } catch (error: any) {
      errorStore.showInvalidContents(`コンテンツID:${contentsId} の取得に失敗しました。${error.message}`);
      return undefined;
    }
  };

  // チェックしたデータの情報を更新する。複数箇所で使うので、関数にしておく
  const applyLatestCheckInfo = async (
    latestContents: ContentReadInfo,
    planId: string,
    contentsType: string,
    latestCheckInfo: { checkedUntilAt?: Date; lastUnreadBoundaryAt?: Date },
  ) => {
    if (!latestCheckInfo) return;

    if (
      !latestContents.checkedUntilAt ||
      (latestCheckInfo.checkedUntilAt && latestCheckInfo.checkedUntilAt > latestContents.checkedUntilAt)
    ) {
      latestContents.checkedUntilAt = latestCheckInfo.checkedUntilAt;
    }

    if (latestCheckInfo.lastUnreadBoundaryAt) {
      latestContents.lastUnreadBoundaryAt = latestCheckInfo.lastUnreadBoundaryAt;
    }

    // Firestoreに保存
    const userId = loginStore.loginInfo!.userId;
    const updatedInfo = new ContentReadInfo(latestContents.checkedUntilAt, latestContents.lastUnreadBoundaryAt);
    await contentReadStore.updateContentReadInfo(userId, planId, contentsType, updatedInfo);
  };

  /**
   * プランごとの未読情報を取得
   *
   * 処理フロー:
   * 1. 初回（checkedUntilが未設定の場合）
   *    - 全データをチェック（入会後経過日数制限なしのみ）
   *    - チェック済みの範囲（checkedUntil〜lastUnreadBoundary）を保存
   *
   * 2. 2回目以降（checkedUntilが設定済みの場合）
   *    - チェック済みの範囲外をチェック（入会後経過日数制限なしのみ）
   *      - checkedUntilより新しいデータをチェック
   *      - lastUnreadBoundaryより古いデータをチェック
   *
   * 3. 上記1, 2で未読が見つからない場合
   *    - 入会後経過日数制限ありのデータを全てチェック
   *    - (入会後経過日数制限ありのデータは少数と仮定)
   *    - ※ オーナーの場合（onlyPublic=false）はこの処理をスキップ
   */
  const getPlanContentsUnread = async (
    latestContentsList: { [contentsType: string]: ContentReadInfo },
    planId: string,
    contentsType: string,
    onlyPublic: boolean,
  ) => {
    const latestContents = latestContentsList[contentsType];
    if (!latestContents) {
      return false;
    }
    const userId = loginStore.loginInfo!.userId;
    const userPlanPriceId = planStore.plan?.contractStatus?.planPriceId;
    const userContractDays = planStore.plan?.contractStatus?.contractDays;

    if (!userId) {
      return false;
    }

    if (!latestContents.checkedUntilAt) {
      // まだ一度もチェックしていない場合は全件チェック（入会後経過日数制限なしのみチェック）
      const result = await repository.checkUnread(
        planId,
        contentsType,
        onlyPublic,
        userId,
        userPlanPriceId,
        userContractDays,
        {
          onlyRestricted: false,
        },
      );
      await applyLatestCheckInfo(latestContents, planId, contentsType, result?.latestCheckInfo);
      if (result.result) {
        return true;
      }
    } else {
      // 2回目以降
      // checkedUntil以降の新規データをチェック（入会後経過日数制限なしのみチェック）
      const result1 = await repository.checkUnread(
        planId,
        contentsType,
        onlyPublic,
        userId,
        userPlanPriceId,
        userContractDays,
        {
          newerThan: latestContents.checkedUntilAt,
          onlyRestricted: false,
        },
      );
      await applyLatestCheckInfo(latestContents, planId, contentsType, result1?.latestCheckInfo);
      if (result1.result) {
        return true;
      }

      // lastUnreadBoundary以前のデータをチェック（入会後経過日数制限なしのみチェック）
      if (latestContents.lastUnreadBoundaryAt) {
        const result2 = await repository.checkUnread(
          planId,
          contentsType,
          onlyPublic,
          userId,
          userPlanPriceId,
          userContractDays,
          {
            olderThan: latestContents.lastUnreadBoundaryAt,
            onlyRestricted: false,
          },
        );
        await applyLatestCheckInfo(latestContents, planId, contentsType, result2?.latestCheckInfo);
        if (result2.result) {
          return true;
        }
      }
    }

    if (!onlyPublic) {
      // オーナーの場合は入会後経過日数制限なしのデータを含めてチェック済みなので、追加でチェックしない
      return false;
    }

    // 未読なし → 入会後経過日数制限ありを全てチェック(入会後経過日数制限なしのデータは少数と仮定)
    const result3 = await repository.checkUnread(
      planId,
      contentsType,
      onlyPublic,
      userId,
      userPlanPriceId,
      userContractDays,
      {
        onlyRestricted: true,
      },
    );
    return result3.result;
  };

  // プランごとの未読情報を全て取得（旧クライアント側実装）
  const getPlanContentsUnreadAllLegacy = async (planId: string, onlyPublic: boolean) => {
    if (!loginStore.isLogin()) {
      return false; // 未ログイン時は未読フラグはない
    }
    // ContentReadStoreの初期化
    const latestContentsList = await contentReadStore.getContentReadInfo(loginStore.loginInfo!.userId, planId);
    if (!latestContentsList) return;

    // 並列処理で性能向上
    const unreadPromises = CONTENT_TYPE_LIST.map(async contentsType => {
      const unread = await getPlanContentsUnread(latestContentsList, planId, contentsType, onlyPublic);
      return { contentsType, unread: unread ?? false };
    });

    const results = await Promise.all(unreadPromises);
    const unreadList = results.reduce(
      (acc, { contentsType, unread }) => {
        acc[contentsType] = unread;
        return acc;
      },
      {} as { [key: string]: boolean },
    );

    hasUnreadArchive.value = unreadList[CONTENT_TYPE.LIVE_ARCHIVE];
    hasUnreadMovie.value = unreadList[CONTENT_TYPE.MOVIE];
    hasUnreadDocument.value = unreadList[CONTENT_TYPE.DOCUMENT];
    hasUnreadLive.value = unreadList[CONTENT_TYPE.LIVE_MOVIE];
    hasUnreadSchedule.value = unreadList[CONTENT_TYPE.LIVE_SCHEDULE];
    hasUnreadArticle.value = unreadList[CONTENT_TYPE.ARTICLE];
  };

  // プランごとの未読情報を全て取得（Cloud Function版）
  const getPlanContentsUnreadAllCloudFunctions = async (planId: string, onlyPublic: boolean) => {
    if (!loginStore.isLogin()) {
      return false; // 未ログイン時は未読フラグはない
    }

    const userId = loginStore.loginInfo!.userId;
    const userPlanPriceId = planStore.plan?.contractStatus?.planPriceId;
    const userContractDays = planStore.plan?.contractStatus?.contractDays;

    try {
      // Cloud Functionを呼び出し
      const result = await repository.checkPlanContentsUnreadAll(
        planId,
        userId,
        onlyPublic,
        userPlanPriceId,
        userContractDays,
      );

      // 結果をフラグに反映
      hasUnreadArchive.value = result.unreadList[CONTENT_TYPE.LIVE_ARCHIVE] ?? false;
      hasUnreadMovie.value = result.unreadList[CONTENT_TYPE.MOVIE] ?? false;
      hasUnreadDocument.value = result.unreadList[CONTENT_TYPE.DOCUMENT] ?? false;
      hasUnreadLive.value = result.unreadList[CONTENT_TYPE.LIVE_MOVIE] ?? false;
      hasUnreadSchedule.value = result.unreadList[CONTENT_TYPE.LIVE_SCHEDULE] ?? false;
      hasUnreadArticle.value = result.unreadList[CONTENT_TYPE.ARTICLE] ?? false;
    } catch (error) {
      // エラーハンドリング
      sentryErrorLog(`getPlanContentsUnreadAll error: ${error}`);
      // エラー時は全て未読なしとする
      hasUnreadArchive.value = false;
      hasUnreadMovie.value = false;
      hasUnreadDocument.value = false;
      hasUnreadLive.value = false;
      hasUnreadSchedule.value = false;
      hasUnreadArticle.value = false;
    }
  };

  // プランごとの未読情報を全て取得（Feature Toggle対応）
  const getPlanContentsUnreadAll = async (planId: string, onlyPublic: boolean) => {
    // Feature Toggleで新旧実装を切り替え
    if (featureCloudFunctionsUnreadCheck()) {
      return await getPlanContentsUnreadAllCloudFunctions(planId, onlyPublic);
    } else {
      return await getPlanContentsUnreadAllLegacy(planId, onlyPublic);
    }
  };

  return {
    userContentsInfo,
    hasUnreadArchive,
    hasUnreadMovie,
    hasUnreadDocument,
    hasUnreadLive,
    hasUnreadSchedule,
    hasUnreadArticle,
    hasUnreadForLiveMenu,
    hasUnreadForPlanTab,
    putPublish,
    postBrowse,
    postComplete,
    postGood,
    deleteGood,
    postBookmark,
    deleteBookmark,
    fetchUserContentsInfo,
    getUserContentsInfo,
    putUserContentsInfo,
    getUserContentsList,
    getUserContentsListAll,
    putCurrentTime,
    putMaxPage,
    getList,
    getDetail,
    getPlanContentsUnreadAll,
  };
});
